<template>
  <GenericCard
    :locked="locked"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="$v.physical.$invalid"
    :dirty="$v.physical.$anyDirty"
    title="Physical Address"
    :section="physical"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <ZipableAddressFields
        v-model="physical"
        class="pa-0 mb-4"
        :locked="locked"
      />
      <v-row>
        <v-col
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Start Date"
            :formatable-date="physical.start_date"
            :format="physical.start_date_format"
            :uncertain="physical.start_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages($v.physical.start_date)"
            @update-box="onStartDateUncertainUpdate"
            @update-date="onStartDateUpdate"
            @tab-out="$v.physical.start_date.$touch()"
          />
        </v-col>
        <v-spacer />
        <v-col cols="auto">
          <v-btn
            color="primary"
            :disabled="locked || emptyOnUnlock"
            @click="moveDialog = true"
          >
            Convert to Residence History
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-dialog
      v-model="moveDialog"
      persistent
      max-width="600"
    >
      <v-card>
        <v-card-title class="text-h5">
          Move Physical Address
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-5 pb-0">
          <v-row>
            <v-col cols="12">
              Are you sure you want to move this address to Residence History?
            </v-col>
            <v-col cols="12">
              <FormatSelectableComboDate
                class="pt-1 pl-1"
                :error-messages="getErrorMessages($v.physical.end_date)"
                :formatable-date="physical.end_date"
                :format="physical.end_date_format"
                label="End Date"
                outlined
                required
                :uncertain="physical.end_date_uncertain"
                @tab-out="$v.physical.end_date.$touch()"
                @update-box="value => onEndDateUncertainUpdate(value)"
                @update-date="value => onEndDateUpdate(value)"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="green darken-1"
            text
            @click="moveDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary darken-1"
            text
            @click="onAddressMove"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { useVuelidate } from '@vuelidate/core'

import { alertHandler } from '@/alertHandler'
import { parseErrorResponseMessage } from '@/utilities/error-responses'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import FormatSelectableComboDate from '@/components/Fields/FormatSelectableComboDate.vue'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard.vue'
import ZipableAddressFields from '@/components/Party/PartyEdit/SectionCards/PersonalDataCards/ZipableAddressFields.vue'

export default {
  name: 'PhysicalAddressCard',

  components: {
    FormatSelectableComboDate,
    GenericCard,
    ZipableAddressFields,
  },

  props: {
    item: {
      type: Object,
      default: null,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      $v: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      cached_a_number: null,
      cached_ssn: null,
      emptyOnUnlock: false,
      moveDialog: false,
      section: {},
    }
  },

  validations () {
    return {
      physical: {
        address: {},
        address_type: {},
        city: {},
        country_id: {},
        county: {},
        end_date: {},
        end_date_uncertain: {},
        start_date: {},
        start_date_uncertain: {},
        state: {},
        zip: {},
      },
    }
  },

  computed: {
    cardIdentifier () { return 'physical-address-card' },

    locked: {
      get () {
        return this.pePhysicalAddressCardLock
      },

      set (value) {
        this.setPePhysicalAddressCardLock(value)
      },
    },

    optionsStates () { return this.getCachedDropdown('us_state/states') },
    physical () { return this.section || {} },

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),

    ...mapGetters(PARTY_NAMESPACE, [
      'pePhysicalAddressCardIsEmpty',
      'pePhysicalAddressCardLock',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    this.initPePhysicalAddressCard()

    this.getDropdownOptions({ path: 'us_state/states' })
    this.getDropdownOptions({ options: false, path: 'countries' })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onAddressMove () {
      this.moveDialog = false
      return this.pePhysicalAddressMoveToHistory({ id: this.section.id, section: this.physical }).
        then(_ => this.displayAlert('success', 'Physical Address moved to Residence History')).
        catch(e => this.displayAlert('error', parseErrorResponseMessage(e)))
    },

    async onCardRefresh () {
      return this.pePhysicalAddressCardRefresh({ id: this.section.id })
    },

    async onCardUpdate () {
      return this.pePhysicalAddressCardUpdate({ id: this.section.id, section: this.physical })
    },

    onEndDateUncertainUpdate (value) {
      this.$v.physical.end_date.$touch()
      this.$v.physical.end_date_uncertain.$touch()
      this.physical.end_date_uncertain = value
    },

    onEndDateUpdate (value) {
      this.$v.physical.end_date.$touch()
      this.$v.physical.end_date_uncertain.$touch()
      this.physical.end_date = value.date
      this.physical.end_date_format = value.format
    },

    async onSectionUnlock () {
      this.emptyOnUnlock = this.pePhysicalAddressCardIsEmpty
      return this.setPePhysicalAddressCardLock(false)
    },

    onStartDateUncertainUpdate (value) {
      this.$v.physical.start_date.$touch()
      this.$v.physical.start_date_uncertain.$touch()
      this.physical.start_date_uncertain = value
    },

    onStartDateUpdate (value) {
      this.$v.physical.start_date.$touch()
      this.$v.physical.start_date_uncertain.$touch()
      this.physical.start_date = value.date
      this.physical.start_date_format = value.format
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'setPePhysicalAddressCardLock',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPePhysicalAddressCard',
      'pePhysicalAddressCardRefresh',
      'pePhysicalAddressCardUpdate',
      'pePhysicalAddressMoveToHistory',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
